import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type {
  EarlyUnlockSavingsVaultRequestDto,
  SavingsVaultDto,
} from "@/features/savings/api/dto";

/**
 * Déblocage anticipé d'un coffre (`POST /SavingsVaults/{id}/early-unlock`)
 * — action back-office en DOUBLE VALIDATION : exige une demande
 * `SAVINGS_EARLY_UNLOCK` (resourceType `SAVINGS_VAULT`, resourceId = coffre)
 * au statut APPROVED, vérifiée en direct auprès d'admin-service ; la demande
 * passe ensuite en APPLIED. Rejouer avec la même approbation est sans effet.
 * Rôles : `flashpay-admin` / `flashpay-superadmin`.
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, clé persistée par empreinte du
 * corps). 422 : coffre non bloqué, approbation d'un autre type / coffre ou
 * non approuvée.
 */
export const useEarlyUnlockSavingsVault = (vaultId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "early-unlock", vaultId],
    mutationFn: (body: EarlyUnlockSavingsVaultRequestDto) =>
      mutateIdempotent<SavingsVaultDto>({
        scopeId: `savings.savingsVaults.earlyUnlock.${vaultId}`,
        url: savingsEndpoints.savingsVaults.earlyUnlock(vaultId),
        body,
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(vaultId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useEarlyUnlockSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
